import type { ApiCategory } from '@/components/personalizedCategoryCarousel/apiCategories'

import { getMagicNumber, SELLER_EXCLUSIONS } from './constants'

/**
 * gap = magic number − unique bidders. `null` means "unknown" (no bidder
 * data for this listing, e.g. the auth-gated auction-api augmentation
 * failed or the caller isn't authenticated) — callers should skip these
 * listings rather than treat them as eligible.
 */
export const computeGap = (
  category: ApiCategory | null | undefined,
  uniqueBidders: number | null | undefined
): number | null => {
  if (uniqueBidders == null) return null
  return getMagicNumber(category) - uniqueBidders
}

export const isExcludedSeller = (
  category: ApiCategory | null | undefined,
  storefrontName: string | null | undefined
): boolean => {
  if (!category || !storefrontName) return false
  return SELLER_EXCLUSIONS[category]?.includes(storefrontName) ?? false
}

type GapCandidate<T> = {
  listing: T
  gap: number
  msRemaining: number
}

/**
 * Ranks by gap size weighted by urgency (time remaining). A listing with a
 * bigger gap and less time left outranks one with the same gap but a full
 * week remaining — the buyer's click is worth more to a listing that's
 * about to close undersubscribed than one that has time to fill up anyway.
 *
 * Urgency decays smoothly over roughly a day: at 0h remaining the weight is
 * 1; at 24h it's 0.5; it keeps shrinking (never hits 0) for longer windows.
 */
export const rankCandidates = <T>(
  candidates: Array<GapCandidate<T>>
): Array<GapCandidate<T>> => {
  const urgencyWeight = (msRemaining: number) => {
    const hoursRemaining = Math.max(msRemaining, 0) / (60 * 60 * 1000)
    return 1 / (1 + hoursRemaining / 24)
  }
  const score = (c: GapCandidate<T>) => c.gap * urgencyWeight(c.msRemaining)

  return [...candidates].sort((a, b) => score(b) - score(a))
}
